import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { PathType, ProjectDetail, ProjectListItem } from '../api/types';

export const projectKeys = {
  all: ['projects'] as const,
  list: (status?: 'active' | 'archived' | 'all') => ['projects', 'list', status ?? 'all'] as const,
  detail: (id: string) => ['projects', 'detail', id] as const,
};

export function useProjects(status?: 'active' | 'archived' | 'all') {
  return useQuery({
    queryKey: projectKeys.list(status),
    queryFn: async (): Promise<ProjectListItem[]> => {
      const res = await apiClient.get<{ projects: ProjectListItem[] }>('/projects', {
        params: status ? { status } : undefined,
      });
      return res.data.projects;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useProject(id: string | null) {
  return useQuery({
    queryKey: projectKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: async (): Promise<ProjectDetail> => {
      const res = await apiClient.get<ProjectDetail>(`/projects/${id}`);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface CreateProjectInputPathA {
  pathType: 's1000d';
  name: string;
  s1000dVersion: string;
  modelIc: string;
  securityClassificationId: string;
  languageCodeId: string;
  countryCodeId: string;
  icnType: 'mi' | 'cage';
  originatorCageId: string;
  rpcCageId: string;
  brexConfig: {
    system: string;
    subSystem: string;
    subSubSystem: string;
    unit: string;
    dc: string;
    dcv: string;
    infoCode: string;
    icv: string;
    itemLocation: string;
  };
  /**
   * 2026-06-17 — optional multi-model extras seeded at project
   * create time. The backend route validates each value against the
   * shared S1000D modelIdentCode rule, rejects duplicates and the
   * primary-equal case, and inserts `ProjectAllowedModelCode` rows
   * in the same transaction as the project + default BREX. Omit /
   * empty to keep single-model behaviour.
   */
  additionalModelIcs?: string[];
  customWorkflow: boolean;
  workflowConfig?: { roles: string[] };
  snsId?: string;
  labels?: { name: string; color: string }[];
}

// Path B project creation. Phase 2B — description IS now persisted
// (Project.description column added in
// 20260520000000_project_description_and_serial_scope). equipmentId is
// persisted at create time; serial scope + stylesheet are configured
// later from Project Settings + publish time respectively.
export interface CreateProjectInputPathB {
  pathType: 'html';
  name: string;
  description?: string;
  equipmentId?: string | null;
  labels?: { name: string; color: string }[];
}

export type CreateProjectInput = CreateProjectInputPathA | CreateProjectInputPathB;

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProjectInput) => {
      const res = await apiClient.post<{ id: string; name: string; pathType: PathType }>(
        '/projects',
        input,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

// Phase 2C — widen the PATCH payload to cover description (both paths),
// equipmentId (Path B only — backend enforces), and scopedSerialIds (the
// full set of in-scope serials after the change; backend performs a diff
// replace).
//
// Phase 3B — Path A General tab fields per spec/03 §3.4. All four are
// Path A only — backend enforces with `INVALID_REQUEST`.
export interface UpdateProjectInput {
  id: string;
  modelIc?: string;
  status?: 'active' | 'archived';
  labels?: { name: string; color: string }[];
  description?: string | null;
  equipmentId?: string | null;
  scopedSerialIds?: string[];
  securityClassificationId?: string | null;
  languageCodeId?: string | null;
  countryCodeId?: string | null;
  icnType?: 'mi' | 'cage';
  // A15.1 Phase B — Project Settings SNS Pick. Path A only (backend
  // rejects on Path B with INVALID_REQUEST). Null clears ("None"
  // option). Cross-org rejected with 404 SNS_NOT_FOUND.
  snsId?: string | null;
  // `s1000dVersion` is intentionally absent — it's immutable after
  // project creation (spec/03 §3.2 + §3.4). The backend PATCH
  // /projects/:id handler returns `400 IMMUTABLE_FIELD` for any body
  // that carries the key, even with the same value already on the
  // row, so the field must never be sent.
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateProjectInput) => {
      const { id, ...body } = input;
      const res = await apiClient.patch<ProjectDetail>(`/projects/${id}`, body);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: projectKeys.all });
      qc.invalidateQueries({ queryKey: projectKeys.detail(vars.id) });
    },
  });
}

// Phase 3B — spec/03 §3.4 lines 257–276. Read-only DDN Metadata pin
// block on the Project Settings General tab. Returns `{ pinned: null }`
// when no profile is pinned.
export interface ProjectDdnPinned {
  profileId: string;
  name: string;
  originatorAddress: string | null;
  receiptAddress: string | null;
  dispatchReasonDefault: string | null;
  dispatchPriorityDefault: string | null;
  isDefault: boolean;
}

export interface ProjectDdnPinResponse {
  pinned: ProjectDdnPinned | null;
}

// IMPORT-DISPLAY-1 (2026-06-12) — read-only project import summary.
// Drives the post-import verification panel. Counts are computed live
// from the project's data_modules / icn_records / applicability_dms /
// cir_dms tables — see `src/routes/projects.ts:/:id/import-summary`.
export interface ProjectImportSummaryResponse {
  dataModules: number;
  autoBrexDataModules: number;
  icns: number;
  applicability: { act: number; cct: number; pct: number };
  cir: { warning: number; caution: number; supply: number; part: number; tool: number };
  // Phase 2 Block 5 — DMs imported as DM-only because the structured
  // editor for their S1000D common repository type is not available
  // yet. `kind` is the literal root body element (e.g. `zoneRepository`).
  // Kept as a single flat list for backward compatibility; new clients
  // should prefer the two split fields below.
  unsupportedRepositories: Array<{ kind: string; count: number }>;
  // 2026-06-13 — truthful taxonomy split. `commonRepositoryDmOnly`
  // carries the 5 recognised S1000D common repository kinds the CIR /
  // Common Repository area surfaces. `applicabilityCatalogDmOnly`
  // carries the `applicCrossRefTableCatalog` ACT-area catalog DM
  // (null when the project has none).
  commonRepositoryDmOnly: Array<{ kind: string; label: string; count: number }>;
  applicabilityCatalogDmOnly: { kind: string; label: string; count: number } | null;
}

export function useProjectImportSummary(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'import-summary', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<ProjectImportSummaryResponse> => {
      const res = await apiClient.get<ProjectImportSummaryResponse>(
        `/projects/${projectId}/import-summary`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

// 2026-06-13 — per-project listing of the DMs whose
// `unsupportedRepositoryKind` falls into the new taxonomy buckets.
// Drives the read-only "Common repository DMs" panel under CIR and
// the "ACT Catalogs" panel under Applicability.
export interface DmOnlyRow {
  id: string;
  dmc: string;
  techName: string | null;
  infoName: string | null;
  status: string;
  issueNumber: string;
  inWork: string;
}
export interface DmOnlyRepositoriesResponse {
  commonRepositoryDmOnly: Array<{
    kind: string;
    label: string;
    dms: DmOnlyRow[];
  }>;
  applicabilityCatalogDmOnly: {
    kind: string;
    label: string;
    dms: DmOnlyRow[];
  };
}

export function useDmOnlyRepositories(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'dm-only-repositories', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<DmOnlyRepositoriesResponse> => {
      const res = await apiClient.get<DmOnlyRepositoriesResponse>(
        `/projects/${projectId}/dm-only-repositories`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

// 2026-06-13 — structured projection of the project's zoneRepository
// DM + project-wide zoneRef usage scan. Phase 2 adds:
//   • `dm.zoneRepositoryDmId` — the side-table parent ID that CRUD
//     mutations target.
//   • `dm.checkedOutByMe` — gates the editor on the client.
//   • `dm.backedBySideTable` — when false, the response falls back to
//     the read-only XML projection (Phase 1 contract).
//   • per-row `id` (nullable when side table is empty) + `refs[]`
//     carrying every `<zoneRefGroup>`, not just the first.
export interface ZoneRefGroupDto {
  zoneRefType: string;
  zoneNumbers: string[];
}
export interface ZoneSpecDto {
  id: string | null;
  zoneNumber: string;
  zoneType: string | null;
  refGroup: { zoneRefType: string; targets: string[] } | null;
  refs: ZoneRefGroupDto[];
  alt: { applicRefId: string | null; itemDescr: string | null } | null;
  usages: Array<{ dmId: string; dmc: string; parent: string | null }>;
}
export interface ZoneRepositoryResponse {
  dm: {
    id: string;
    dmc: string;
    techName: string | null;
    infoName: string | null;
    status: string;
    issueNumber: string;
    inWork: string;
    checkedOutByMe: boolean;
    zoneRepositoryDmId: string | null;
    backedBySideTable: boolean;
  } | null;
  zones: ZoneSpecDto[];
  adHocReferences: Array<{
    zoneNumber: string;
    usages: Array<{ dmId: string; dmc: string; parent: string | null }>;
  }>;
}

export function useZoneRepository(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'zone-repository', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<ZoneRepositoryResponse> => {
      const res = await apiClient.get<ZoneRepositoryResponse>(
        `/projects/${projectId}/zone-repository`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

// 2026-06-13 — controlIndicatorRepository structured projection.
// Phase 1 shipped read-only; Phase 2 surfaces side-table-backed rows
// (with per-row IDs) + checkedOutByMe gating + descrHasInlineMarkup so
// the UI can lock the function field on rows whose original content
// carries `<inlineSignificantData>` etc. that the structured editor
// cannot safely round-trip.
export interface ControlIndicatorSpecDto {
  id: string | null;
  controlIndicatorNumber: string;
  applicRefId: string | null;
  key: string;
  nameXml: string;
  shortName: string | null;
  descrXml: string;
  descrHasInlineMarkup: boolean;
}
export interface ControlIndicatorGroupDto {
  internalRefs: Array<{
    internalRefId: string;
    internalRefTargetType: string | null;
  }>;
  specs: ControlIndicatorSpecDto[];
}
export interface ControlIndicatorRepositoryResponse {
  dm: {
    id: string;
    dmc: string;
    techName: string | null;
    infoName: string | null;
    status: string;
    issueNumber: string;
    inWork: string;
    checkedOutByMe: boolean;
    controlIndicatorRepositoryDmId: string | null;
    backedBySideTable: boolean;
  } | null;
  groups: ControlIndicatorGroupDto[];
}

export function useControlIndicatorRepository(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'control-indicator-repository', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<ControlIndicatorRepositoryResponse> => {
      const res = await apiClient.get<ControlIndicatorRepositoryResponse>(
        `/projects/${projectId}/control-indicator-repository`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

// Phase 2 (2026-06-13) — control indicator item mutations. Each
// invalidates the repository query so the table reflects the new
// catalog immediately.
export interface ControlIndicatorMutationBody {
  controlIndicatorNumber: string;
  applicRefId?: string | null;
  key: string;
  name: string;
  shortName?: string | null;
  descr?: string | null;
}
function invalidateCi(qc: ReturnType<typeof useQueryClient>, projectId: string): void {
  void qc.invalidateQueries({
    queryKey: ['projects', 'control-indicator-repository', projectId],
  });
}
export function useCreateControlIndicatorItem(
  projectId: string,
  repoDmId: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ControlIndicatorMutationBody) => {
      if (!repoDmId) throw new Error('Control indicator repository DM id is missing');
      const res = await apiClient.post(
        `/projects/${projectId}/control-indicator-repository/${repoDmId}/items`,
        body,
      );
      return res.data;
    },
    onSuccess: () => invalidateCi(qc, projectId),
  });
}
export function useUpdateControlIndicatorItem(
  projectId: string,
  repoDmId: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; body: Partial<ControlIndicatorMutationBody> }) => {
      if (!repoDmId) throw new Error('Control indicator repository DM id is missing');
      const res = await apiClient.patch(
        `/projects/${projectId}/control-indicator-repository/${repoDmId}/items/${vars.itemId}`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: () => invalidateCi(qc, projectId),
  });
}
export function useDeleteControlIndicatorItem(
  projectId: string,
  repoDmId: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      if (!repoDmId) throw new Error('Control indicator repository DM id is missing');
      await apiClient.delete(
        `/projects/${projectId}/control-indicator-repository/${repoDmId}/items/${itemId}`,
      );
    },
    onSuccess: () => invalidateCi(qc, projectId),
  });
}

// 2026-06-13 Phase 2 — Functional/Physical Area / SNS Repository.
// Side-table-backed read + CRUD. The natural key per parent is the
// canonicalised identifier tuple; `sourceId` (the optional `@id`
// attribute) is surfaced separately because the Bike sample's
// `sns-NNN` ids are the operator-friendly handle.
export interface FpaIdentifierDto {
  systemDiffCode: string | null;
  systemCode: string;
  subSystemCode: string | null;
  subSubSystemCode: string | null;
  assyCode: string | null;
  disassyCode: string | null;
  disassyCodeVariant: string | null;
}
export interface FpaSpecDto {
  id: string | null;
  sourceId: string | null;
  applicRefId: string | null;
  ident: FpaIdentifierDto;
  nameXml: string | null;
  shortName: string | null;
  refs: FpaIdentifierDto[];
  nameHasInlineMarkup: boolean;
}
export interface FpaRepositoryResponse {
  dm: {
    id: string;
    dmc: string;
    techName: string | null;
    infoName: string | null;
    status: string;
    issueNumber: string;
    inWork: string;
    checkedOutByMe: boolean;
    functionalPhysicalAreaRepositoryDmId: string | null;
    backedBySideTable: boolean;
  } | null;
  specs: FpaSpecDto[];
}

export function useFunctionalPhysicalAreaRepository(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'functional-physical-area-repository', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<FpaRepositoryResponse> => {
      const res = await apiClient.get<FpaRepositoryResponse>(
        `/projects/${projectId}/functional-physical-area-repository`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

export interface FpaMutationBody {
  sourceId?: string | null;
  applicRefId?: string | null;
  ident: FpaIdentifierDto;
  name?: string | null;
  shortName?: string | null;
  refs?: FpaIdentifierDto[];
}
function invalidateFpa(qc: ReturnType<typeof useQueryClient>, projectId: string): void {
  void qc.invalidateQueries({
    queryKey: ['projects', 'functional-physical-area-repository', projectId],
  });
}
export function useCreateFpaSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: FpaMutationBody) => {
      if (!repoDmId) throw new Error('FPA repository DM id is missing');
      const res = await apiClient.post(
        `/projects/${projectId}/functional-physical-area-repository/${repoDmId}/specs`,
        body,
      );
      return res.data;
    },
    onSuccess: () => invalidateFpa(qc, projectId),
  });
}
export function useUpdateFpaSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; body: Partial<FpaMutationBody> }) => {
      if (!repoDmId) throw new Error('FPA repository DM id is missing');
      const res = await apiClient.patch(
        `/projects/${projectId}/functional-physical-area-repository/${repoDmId}/specs/${vars.itemId}`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: () => invalidateFpa(qc, projectId),
  });
}
export function useDeleteFpaSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      if (!repoDmId) throw new Error('FPA repository DM id is missing');
      await apiClient.delete(
        `/projects/${projectId}/functional-physical-area-repository/${repoDmId}/specs/${itemId}`,
      );
    },
    onSuccess: () => invalidateFpa(qc, projectId),
  });
}

// 2026-06-13 Phase 2 — Functional Item Repository. Spec + alt CRUD.
// Nested `<location>` and `<functionalItemFamily>` content stays raw
// (locationXml / familyXml strings) — the editor never auto-edits
// them; operators paste from raw XML mode.
export interface FiIdentDto {
  functionalItemNumber: string;
  functionalItemType: string;
  sourceId: string | null;
  installationIdent: string | null;
  contextIdent: string | null;
  manufacturerCodeValue: string | null;
  itemOriginator: string | null;
}
export interface FiAltDto {
  sourceId: string | null;
  applicRefId: string | null;
  normativeComponentFlag: string | null;
  sealedFlag: string | null;
  locationXml: string | null;
  familyXml: string | null;
}
export interface FiSpecDto {
  id: string | null;
  identKey: string;
  ident: FiIdentDto;
  nameXml: string;
  shortName: string | null;
  alts: FiAltDto[];
  nameHasInlineMarkup: boolean;
}
export interface FiRepositoryResponse {
  dm: {
    id: string;
    dmc: string;
    techName: string | null;
    infoName: string | null;
    status: string;
    issueNumber: string;
    inWork: string;
    checkedOutByMe: boolean;
    functionalItemRepositoryDmId: string | null;
    backedBySideTable: boolean;
  } | null;
  repositorySourceId: string | null;
  specs: FiSpecDto[];
}

export function useFunctionalItemRepository(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'functional-item-repository', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<FiRepositoryResponse> => {
      const res = await apiClient.get<FiRepositoryResponse>(
        `/projects/${projectId}/functional-item-repository`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

export interface FiSpecBodyDto {
  ident: FiIdentDto;
  name?: string | null;
  shortName?: string | null;
}
export interface FiAltBodyDto {
  sourceId?: string | null;
  applicRefId?: string | null;
  normativeComponentFlag?: string | null;
  sealedFlag?: string | null;
  locationXml?: string | null;
  familyXml?: string | null;
}
function invalidateFi(qc: ReturnType<typeof useQueryClient>, projectId: string): void {
  void qc.invalidateQueries({
    queryKey: ['projects', 'functional-item-repository', projectId],
  });
}
export function useCreateFiSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: FiSpecBodyDto) => {
      if (!repoDmId) throw new Error('FI repository DM id is missing');
      const res = await apiClient.post(
        `/projects/${projectId}/functional-item-repository/${repoDmId}/specs`,
        body,
      );
      return res.data;
    },
    onSuccess: () => invalidateFi(qc, projectId),
  });
}
export function useUpdateFiSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; body: Partial<FiSpecBodyDto> }) => {
      if (!repoDmId) throw new Error('FI repository DM id is missing');
      const res = await apiClient.patch(
        `/projects/${projectId}/functional-item-repository/${repoDmId}/specs/${vars.itemId}`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: () => invalidateFi(qc, projectId),
  });
}
export function useDeleteFiSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      if (!repoDmId) throw new Error('FI repository DM id is missing');
      await apiClient.delete(
        `/projects/${projectId}/functional-item-repository/${repoDmId}/specs/${itemId}`,
      );
    },
    onSuccess: () => invalidateFi(qc, projectId),
  });
}
export function useCreateFiAlt(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; body: FiAltBodyDto }) => {
      if (!repoDmId) throw new Error('FI repository DM id is missing');
      const res = await apiClient.post(
        `/projects/${projectId}/functional-item-repository/${repoDmId}/specs/${vars.itemId}/alts`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: () => invalidateFi(qc, projectId),
  });
}
export function useUpdateFiAlt(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; altIndex: number; body: Partial<FiAltBodyDto> }) => {
      if (!repoDmId) throw new Error('FI repository DM id is missing');
      const res = await apiClient.patch(
        `/projects/${projectId}/functional-item-repository/${repoDmId}/specs/${vars.itemId}/alts/${vars.altIndex}`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: () => invalidateFi(qc, projectId),
  });
}
export function useDeleteFiAlt(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; altIndex: number }) => {
      if (!repoDmId) throw new Error('FI repository DM id is missing');
      await apiClient.delete(
        `/projects/${projectId}/functional-item-repository/${repoDmId}/specs/${vars.itemId}/alts/${vars.altIndex}`,
      );
    },
    onSuccess: () => invalidateFi(qc, projectId),
  });
}

// 2026-06-13 Phase 2 — Applicability Repository (final checkpoint).
// applicRepository is NOT ACT/CCT/PCT and NOT ACT Catalog — it is
// the registry tying applicIdentValue strings to applic map
// references. Surfaced under the Applicability section with the
// honest label "Applicability Repository".
export interface ApplicSpecDto {
  id: string | null;
  sourceId: string | null;
  applicMapRefId: string;
  applicIdentValue: string;
  refsXml: string | null;
}
export interface ApplicRepositoryResponse {
  dm: {
    id: string;
    dmc: string;
    techName: string | null;
    infoName: string | null;
    status: string;
    issueNumber: string;
    inWork: string;
    checkedOutByMe: boolean;
    applicRepositoryDmId: string | null;
    backedBySideTable: boolean;
  } | null;
  specs: ApplicSpecDto[];
}

export function useApplicRepository(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'applic-repository', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<ApplicRepositoryResponse> => {
      const res = await apiClient.get<ApplicRepositoryResponse>(
        `/projects/${projectId}/applic-repository`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

export interface ApplicSpecBodyDto {
  sourceId?: string | null;
  applicMapRefId: string;
  applicIdentValue: string;
  refsXml?: string | null;
}
function invalidateAr(qc: ReturnType<typeof useQueryClient>, projectId: string): void {
  void qc.invalidateQueries({
    queryKey: ['projects', 'applic-repository', projectId],
  });
}
export function useCreateApplicSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ApplicSpecBodyDto) => {
      if (!repoDmId) throw new Error('Applic repository DM id is missing');
      const res = await apiClient.post(
        `/projects/${projectId}/applic-repository/${repoDmId}/specs`,
        body,
      );
      return res.data;
    },
    onSuccess: () => invalidateAr(qc, projectId),
  });
}
export function useUpdateApplicSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; body: Partial<ApplicSpecBodyDto> }) => {
      if (!repoDmId) throw new Error('Applic repository DM id is missing');
      const res = await apiClient.patch(
        `/projects/${projectId}/applic-repository/${repoDmId}/specs/${vars.itemId}`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: () => invalidateAr(qc, projectId),
  });
}
export function useDeleteApplicSpec(projectId: string, repoDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      if (!repoDmId) throw new Error('Applic repository DM id is missing');
      await apiClient.delete(
        `/projects/${projectId}/applic-repository/${repoDmId}/specs/${itemId}`,
      );
    },
    onSuccess: () => invalidateAr(qc, projectId),
  });
}

// Phase 2 (2026-06-13) — zone item mutations. Each invalidates the
// zone repository query so the table re-renders with the new refs +
// referenced-by counts immediately.
export interface ZoneItemMutationBody {
  zoneNumber: string;
  zoneType?: string | null;
  itemDescr?: string | null;
  applicRefId?: string | null;
  refs?: ZoneRefGroupDto[];
}
function invalidateZone(qc: ReturnType<typeof useQueryClient>, projectId: string): void {
  void qc.invalidateQueries({ queryKey: ['projects', 'zone-repository', projectId] });
}

export function useCreateZoneItem(projectId: string, zoneRepositoryDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ZoneItemMutationBody) => {
      if (!zoneRepositoryDmId) throw new Error('Zone repository DM id is missing');
      const res = await apiClient.post(
        `/projects/${projectId}/zone-repository/${zoneRepositoryDmId}/items`,
        body,
      );
      return res.data;
    },
    onSuccess: () => invalidateZone(qc, projectId),
  });
}
export function useUpdateZoneItem(projectId: string, zoneRepositoryDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; body: Partial<ZoneItemMutationBody> }) => {
      if (!zoneRepositoryDmId) throw new Error('Zone repository DM id is missing');
      const res = await apiClient.patch(
        `/projects/${projectId}/zone-repository/${zoneRepositoryDmId}/items/${vars.itemId}`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: () => invalidateZone(qc, projectId),
  });
}
export function useDeleteZoneItem(projectId: string, zoneRepositoryDmId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      if (!zoneRepositoryDmId) throw new Error('Zone repository DM id is missing');
      await apiClient.delete(
        `/projects/${projectId}/zone-repository/${zoneRepositoryDmId}/items/${itemId}`,
      );
    },
    onSuccess: () => invalidateZone(qc, projectId),
  });
}

export function useProjectDdnPin(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'ddn-pin', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<ProjectDdnPinResponse> => {
      const res = await apiClient.get<ProjectDdnPinResponse>(
        `/projects/${projectId}/ddn-pin`,
      );
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

// Phase 2C — Project Settings Serials & Equipment tab. Fetches the
// per-project serial list (incl. inScope flag).
import type { ProjectSerial } from '../api/types';

export function useProjectSerials(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'serials', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<ProjectSerial[]> => {
      const res = await apiClient.get<{ serials: ProjectSerial[] }>(
        `/projects/${projectId}/serials`,
      );
      return res.data.serials;
    },
    staleTime: 60 * 1000,
  });
}

// A15.1 Phase B — CSDB SnsPane fetch. Backend route returns
// `{ snsId: null }` when the project has no SNS assigned (used to
// render the empty state with a Generate prompt when DMs exist),
// or `{ snsId, name, tree }` when assigned. `tree` follows
// SnsTreeData from the backend service.

export interface SnsTreeNodeDto {
  code: string;
  title: string;
  children: SnsTreeNodeDto[];
}

// CSDB-SNS-COVERAGE-1 (2026-06-06) — header-summary shape returned
// alongside `tree`. Present only when `tree` has nodes; omitted
// otherwise so the empty-state branches stay typed without a "0/0"
// fallback that would read oddly in the UI.
export interface SnsCoverageDto {
  totalNodes: number;
  realizedCoveredNodes: number;
  plannedCoveredNodes: number;
  combinedCoveredNodes: number;
}

export interface ProjectSnsTreeResponse {
  snsId: string | null;
  name?: string;
  tree?: {
    version: number;
    source: 'import-xml' | 'generate-from-dms' | string;
    nodes: SnsTreeNodeDto[];
  } | null;
  coverage?: SnsCoverageDto;
}

export function useProjectSnsTree(projectId: string | null) {
  return useQuery({
    queryKey: ['projects', 'sns-tree', projectId ?? ''],
    enabled: !!projectId,
    queryFn: async (): Promise<ProjectSnsTreeResponse> => {
      const res = await apiClient.get<ProjectSnsTreeResponse>(
        `/projects/${projectId}/sns-tree`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

// A15.1 Phase C — Generate SNS from current project structure.
// Two-step UX: preview (no DB write) → user confirms a name →
// commit (creates sns_definitions row + assigns to project; 409
// SNS_ALREADY_ASSIGNED if project already has snsId set).
//
// Source selector (added 2026-06-06):
//   - 'dms'      — default; walks data_modules only. Migration aid
//                  for legacy projects with realised DMs but no SNS.
//   - 'dmrl'     — walks dmrl_entries (planned + realized); recovers
//                  projects that planned DMRL rows BEFORE assigning
//                  an SNS.
//   - 'combined' — DMs + planned DMRL rows; realized DMRL rows are
//                  represented via their linked DMs (no double-count).

export type SnsGenerateSource = 'dms' | 'dmrl' | 'combined';

export interface GeneratedSnsPreview {
  source: SnsGenerateSource;
  // Per-source row counts. Always both fields, zero when the source
  // doesn't apply, so the preview-summary text can render
  // unconditionally.
  dmCount: number;
  dmrlCount: number;
  // How many tree nodes received a non-empty title. Only DMRL data
  // contributes titles today — DMs are code-only.
  titledLeafCount: number;
  nodeCount: number;
  maxDepth: number;
  tree: ProjectSnsTreeResponse['tree'];
}

export function useGenerateSnsPreview() {
  return useMutation({
    mutationFn: async (params: {
      projectId: string;
      source?: SnsGenerateSource;
    }): Promise<GeneratedSnsPreview> => {
      const res = await apiClient.post<GeneratedSnsPreview>(
        `/projects/${params.projectId}/sns/generate-preview`,
        { source: params.source ?? 'dms' },
      );
      return res.data;
    },
  });
}

export function useGenerateSnsCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      projectId: string;
      name: string;
      source?: SnsGenerateSource;
    }) => {
      const res = await apiClient.post<{ snsId: string; nodeCount: number; maxDepth: number }>(
        `/projects/${params.projectId}/sns/generate-commit`,
        { name: params.name, source: params.source ?? 'dms' },
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['projects', 'sns-tree', vars.projectId] });
      qc.invalidateQueries({ queryKey: projectKeys.detail(vars.projectId) });
    },
  });
}

// 2026-06-14 multi-model project support — allowed-model-codes
// CRUD hooks for the Project Settings General tab. Primary
// `Project.modelIc` is NEVER stored in the extras table; the server
// helper always returns `[primary, ...extras]` as `allowed`.
export interface AllowedModelCodeDto {
  id: string;
  modelIc: string;
  notes: string | null;
  inUse: {
    dataModules: number;
    dmrlEntries: number;
    publicationModules: number;
  };
  createdAt: string;
  updatedAt: string;
}
export interface AllowedModelCodesResponse {
  primary: string | null;
  extras: AllowedModelCodeDto[];
  allowed: string[];
  multiModel: boolean;
}
const allowedModelCodesKey = (projectId: string) => [
  'projects',
  'allowed-model-codes',
  projectId,
];
export function useAllowedModelCodes(projectId: string | null) {
  return useQuery({
    queryKey: allowedModelCodesKey(projectId ?? ''),
    enabled: !!projectId,
    queryFn: async (): Promise<AllowedModelCodesResponse> => {
      const res = await apiClient.get<AllowedModelCodesResponse>(
        `/projects/${projectId}/allowed-model-codes`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}
function invalidateAllowedCodes(
  qc: ReturnType<typeof useQueryClient>,
  projectId: string,
): void {
  void qc.invalidateQueries({ queryKey: allowedModelCodesKey(projectId) });
}
export function useAddAllowedModelCode(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { modelIc: string; notes?: string | null }) => {
      const res = await apiClient.post<AllowedModelCodeDto>(
        `/projects/${projectId}/allowed-model-codes`,
        body,
      );
      return res.data;
    },
    onSuccess: () => invalidateAllowedCodes(qc, projectId),
  });
}
export function useUpdateAllowedModelCode(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { codeId: string; body: { notes?: string | null } }) => {
      const res = await apiClient.patch<AllowedModelCodeDto>(
        `/projects/${projectId}/allowed-model-codes/${vars.codeId}`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: () => invalidateAllowedCodes(qc, projectId),
  });
}
export function useDeleteAllowedModelCode(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (codeId: string) => {
      await apiClient.delete(
        `/projects/${projectId}/allowed-model-codes/${codeId}`,
      );
    },
    onSuccess: () => invalidateAllowedCodes(qc, projectId),
  });
}
